import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";

// The one Access Denied screen for the whole app, shown inside the portal
// layout so the sidebar and header stay usable.
//
// Used both by the route guard (PermissionOnComponent) and by pages whose API
// call comes back 403 — those used to raise a bare "Insufficient permissions"
// toast that vanished after a few seconds and left an empty page behind.
//
// `module` names the section that was refused, `permissions` lists the codes an
// administrator has to grant, and `homeLink` is where "Go to Dashboard" points
// (the portal differs between the society and controller apps).
const AccessDenied = ({
    module,
    permissions = [],
    message,
    homeLink = "/",
    onRetry
}) => {
    const navigate = useNavigate();
    const codes = (Array.isArray(permissions) ? permissions : [permissions]).filter(Boolean);

    return (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-px-4">
            <div className="tw-w-full tw-max-w-xl tw-bg-white tw-rounded-2xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-8 tw-text-center">
                <div className="tw-w-16 tw-h-16 tw-rounded-2xl tw-bg-red-50 tw-flex tw-items-center tw-justify-center tw-mx-auto">
                    <Icon icon="mdi:shield-lock-outline" className="tw-text-3xl tw-text-red-500" />
                </div>

                <h2 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-4 tw-mb-1">Access Denied</h2>
                <p className="tw-text-sm tw-text-gray-500 tw-mb-1">
                    {message || (module
                        ? `Your role does not have access to ${module}.`
                        : "Your role does not have access to this page.")}
                </p>
                <p className="tw-text-xs tw-text-gray-400">
                    Ask your society administrator to grant it under Settings &gt; Roles &amp; Rights.
                </p>

                {codes.length > 0 && (
                    <div className="tw-mt-4 tw-bg-gray-50 tw-rounded-xl tw-p-3 tw-text-left">
                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
                            Permission required
                        </p>
                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                            {codes.map((code) => (
                                <code
                                    key={code}
                                    className="tw-text-[11px] tw-bg-white tw-border tw-border-gray-200 tw-text-gray-600 tw-px-1.5 tw-py-0.5 tw-rounded"
                                >
                                    {code}
                                </code>
                            ))}
                        </div>
                    </div>
                )}

                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-mt-6">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-sm" />
                        Go Back
                    </button>
                    <Link
                        to={homeLink}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-lg hover:tw-bg-blue-700 tw-no-underline"
                    >
                        <Icon icon="mdi:view-dashboard-outline" className="tw-text-sm" />
                        Go to Dashboard
                    </Link>
                    {onRetry && (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50"
                        >
                            <Icon icon="mdi:refresh" className="tw-text-sm" />
                            Try Again
                        </button>
                    )}
                </div>

                <p className="tw-text-[10px] tw-text-gray-300 tw-mt-4">Error 403</p>
            </div>
        </div>
    );
};

export default AccessDenied;
